import { useCallback } from 'react';
import { bugtracker, useIdentityContext } from '@99designs/design-services-common';
import { BRIEF_PAGE_NAME, sendAddToCartTrackingEvent } from '@99designs/tracking';
import { useBriefSubmittedMutation, useDesignLiveAppointmentUpdatedMutation } from '../../graph';
import { OrphanedBriefError } from '../../lib/errors';
import { hasOverflowFeatureFlag } from '../../lib/experiments';
import { useCart } from '../../lib/hooks';
import { buildDesignLiveAppointmentInput } from '../BriefForm/FormFields/ClientConfiguredFields/DesignLiveAppointmentV1/appointmentValue';
import { BriefRequestFragment } from '../BriefForm/brief.generated';
import { designLiveDataFromBrief } from './requestDataFromBrief';
import { BriefType } from './useHandleSubmit';

type BriefDesignLiveData = ReturnType<typeof designLiveDataFromBrief>;

export const useBriefSubmit = (locale: string) => {
    const { authorizationHeader, shopperId } = useIdentityContext();
    const [briefSubmittedMutation] = useBriefSubmittedMutation();
    const [designLiveAppointmentUpdated] = useDesignLiveAppointmentUpdatedMutation();
    const { isInCart } = useCart(locale, {
        authorizationHeader,
        shopperId,
    });

    return useCallback(
        async (data: BriefType) => {
            const request = data.request;
            if (!request) {
                const orphanedError = new OrphanedBriefError(
                    `Encountered orphaned brief in useBriefSubmit hook: ${data.id}`
                );
                bugtracker.notify(orphanedError, (event) => {
                    event.context = `Error during brief submission for briefId: ${data.id}.`;
                    event.addMetadata('brief', { briefId: data.id });
                });
                throw orphanedError;
            }

            try {
                const briefDesignLiveData = designLiveDataFromBrief(data.dynamicFields);

                if (shouldUpdateDesignLiveAppointment(request, briefDesignLiveData)) {
                    await updateDesignLiveAppointment(
                        request.id,
                        briefDesignLiveData,
                        designLiveAppointmentUpdated
                    );
                }

                const [requestInCart, isOverflowEnabled] = await Promise.all([
                    isInCart(request.id),
                    hasOverflowFeatureFlag(
                        request.expertService?.isOverflowEligible,
                        request.facts?.withDesignLive
                    ),
                ]);

                const { data: requestsData, errors } = await briefSubmittedMutation({
                    variables: {
                        requestId: request.id,
                        isOverflow: isOverflowEnabled ?? false,
                    },
                });

                if (errors && errors.length > 0) {
                    throw new Error(
                        `useBriefSubmit mutation failed: ${errors.map((e) => e.message).join('; ')}`
                    );
                }
                if (!requestsData) throw new Error(`useBriefSubmit: Failed to call to requests`);

                if (!requestInCart) {
                    sendTrackingEvent(request);
                }
            } catch (e) {
                const err = e instanceof Error ? e : new Error(String(e));
                bugtracker.notify(err, (event) => {
                    event.context = `Error during brief submission for requestId: ${request.id}.`;
                    event.addMetadata('requestId', { requestId: request.id });
                });
                throw err;
            }
        },
        [briefSubmittedMutation, designLiveAppointmentUpdated, isInCart]
    );
};

function shouldUpdateDesignLiveAppointment(
    request: BriefRequestFragment,
    briefDesignLiveData: BriefDesignLiveData
) {
    // Always reconcile when a booked time exists; the backend dedupes unchanged
    // appointments. A timeless submit is a no-op rather than a thrown failure.
    return (
        !!request.facts?.withDesignLive &&
        !!briefDesignLiveData.designLiveAppointmentTime &&
        !!briefDesignLiveData.timezone
    );
}

async function updateDesignLiveAppointment(
    requestId: string,
    briefDesignLiveData: BriefDesignLiveData,
    designLiveAppointmentUpdated: ReturnType<typeof useDesignLiveAppointmentUpdatedMutation>[0]
) {
    if (!briefDesignLiveData.timezone || !briefDesignLiveData.designLiveAppointmentTime) {
        throw new Error(
            `design live data is not sufficient to book an appointment: ${JSON.stringify(
                briefDesignLiveData
            )}`
        );
    }

    const { errors } = await designLiveAppointmentUpdated({
        variables: {
            requestId,
            input: buildDesignLiveAppointmentInput(
                briefDesignLiveData.designLiveAppointmentTime,
                briefDesignLiveData.timezone,
                briefDesignLiveData.calendarId
            ),
        },
    });

    if (errors && errors.length > 0) {
        throw errors;
    }
}

function sendTrackingEvent(request: BriefRequestFragment) {
    const productId = request.expertService?.serviceProduct.mpvId;
    const name = request.expertService?.title;
    const coreProductId = request.expertService?.serviceProduct.productKey;
    const priceInDecimal = request.expertService?.price
        ? request.expertService.price.listPriceCents.taxed / 100
        : undefined;
    const currency = request.expertService?.price.currency;

    if (!productId || !name || !coreProductId || !priceInDecimal || !currency) {
        bugtracker.notify(
            new Error(
                `Could not send tracking event for requestId: ${request.id} - missing required fields`
            ),
            (event) => {
                event.addMetadata('tracking', {
                    requestId: request.id,
                    productId,
                    name,
                    coreProductId,
                    price: priceInDecimal,
                    currency,
                });
            }
        );
        return;
    }

    sendAddToCartTrackingEvent({
        pageName: BRIEF_PAGE_NAME,
        pageStage: 'Design',
        pageSection: 'Design Services',
        product_id: productId,
        name,
        core_product_id: coreProductId,
        price: priceInDecimal,
        currency,
    });
}
